import React, { useState, useEffect, useMemo, useCallback } from "react";
import CustomTable from "../../../../../CustomHeadlessTable/CustomTable";
import { unitFromDropdownOrCache } from "../../../../../CustomHeadlessTable/lcaDropdownUnit";
import CustomInputComponent from "../../../../../CustomHeadlessTable/components/CustomInputComponent";
import {
  getAllSurplusTransportA2LCA,
  addSurplusTransportA2LCA,
  updateSurplusTransportA2LCA,
  deleteSurplusTransportA2LCA,
} from "../../../../../../apis/Product/TransportA2Api";
import {
  getProductSurplusTransportA2DisposalMethodDropdown,
  getProductSurplusTransportCountryDropdown,
} from "../../../../../../apis/Dropdown";
import { IconButton } from "@mui/material";
import { KeyboardArrowLeft, KeyboardArrowRight } from "@mui/icons-material";
import { TableCompareTrigger } from "../../../components/lcaComparison";

// Impact categories configuration
const IMPACT_CATEGORIES = [
  { name: "Climate change", unit: "kg CO₂-eq", key: "global_warming" },
  { name: "Ozone depletion", unit: "kg CFC-11-eq", key: "ozone_depletion" },
  { name: "Photochemical ozone formation", unit: "kg NMVOC-eq", key: "smog" },
  { name: "Acidification", unit: "mol H⁺-eq", key: "acidification" },
  {
    name: "Eutrophication, terrestrial",
    unit: "mol N-eq",
    key: "eutrophication_terrestrial",
  },
  {
    name: "Eutrophication, freshwater",
    unit: "kg P-eq",
    key: "eutrophication_freshwater",
  },
  {
    name: "Eutrophication, marine",
    unit: "kg N-eq",
    key: "eutrophication_marine",
  },
  {
    name: "Human toxicity, cancer effects",
    unit: "CTUh",
    key: "carcinogenics",
  },
  {
    name: "Human toxicity, non-cancer effects",
    unit: "CTUh",
    key: "non_carcinogenics",
  },
  {
    name: "Particulate matter",
    unit: "disease incidence",
    key: "respiratory_effects",
  },
  { name: "Ecotoxicity, freshwater", unit: "CTUe", key: "ecotoxicity" },
  {
    name: "Resource use, energy carriers",
    unit: "MJ",
    key: "fossil_fuel_depletion",
  },
  { name: "Land use", unit: "Pt", key: "land_use" },
  { name: "Water use", unit: "m³ world-eq deprived", key: "water_use" },
  {
    name: "Resource use, mineral and metals",
    unit: "kg Sb-eq",
    key: "resource_use_minerals_metals",
  },
  {
    name: "Ionising radiation, human health",
    unit: "kBq U235-eq",
    key: "ionising_radiation",
  },
];

const SurplusMaterialsTable = ({ productId }) => {
  const [data, setData] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [currentImpactCategoryIndex, setCurrentImpactCategoryIndex] =
    useState(0);
  const [disposalMethodDropdown, setDisposalMethodDropdown] = useState([]);
  const [countryDropdown, setCountryDropdown] = useState([]);
  const [unitDisposalMethods, setUnitDisposalMethods] = useState({});

  // Fetch dropdowns
  useEffect(() => {
    getProductSurplusTransportA2DisposalMethodDropdown({
      setDropdown: setDisposalMethodDropdown,
    });
    getProductSurplusTransportCountryDropdown({
      setDropdown: setCountryDropdown,
    });
  }, []);

  // Convert treatment_country code to name when countryDropdown is loaded
  useEffect(() => {
    if (data.length > 0 && countryDropdown.length > 0) {
      setData((prevData) => {
        let hasChanges = false;
        const convertedData = prevData.map((item) => {
          if (
            item.treatment_country &&
            typeof item.treatment_country === "string"
          ) {
            // Check if it's a code (2-3 letter code like "AR") - codes are typically 2-3 characters
            // If it's already a name (longer string), don't convert
            if (item.treatment_country.length <= 3) {
              const foundCountry = countryDropdown.find(
                (option) => option.code === item.treatment_country
              );
              if (
                foundCountry &&
                item.treatment_country !== foundCountry.name
              ) {
                hasChanges = true;
                return {
                  ...item,
                  treatment_country: foundCountry.name, // Display name instead of code
                };
              }
            }
          }
          return item;
        });
        return hasChanges ? convertedData : prevData;
      });
    }
  }, [data, countryDropdown]);

  const toggleColumnVisibility = useCallback((columnId) => {
    setHiddenColumns((prev) =>
      prev.includes(columnId)
        ? prev.filter((id) => id !== columnId)
        : [...prev, columnId]
    );
  }, []);

  // Update units when data or dropdown changes
  useEffect(() => {
    if (data.length > 0 && disposalMethodDropdown.length > 0) {
      const newUnitDisposalMethods = {};
      data.forEach((row) => {
        const disposalMethodCode =
          typeof row.disposal_method === "string"
            ? row.disposal_method
            : row.disposal_method?.value || row.disposal_method?.code;
        if (disposalMethodCode) {
          const selectedMethod = disposalMethodDropdown.find(
            (option) =>
              !option.isCategory &&
              (option.code === disposalMethodCode ||
                option.value === disposalMethodCode)
          );
          if (selectedMethod) {
            newUnitDisposalMethods[row.id] = selectedMethod.unit || "xx";
          }
        }
      });
      setUnitDisposalMethods(newUnitDisposalMethods);
    }
  }, [data, disposalMethodDropdown]);

  const currentImpactCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];

  const handlePreviousImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === 0 ? IMPACT_CATEGORIES.length - 1 : prev - 1
    );
  }, []);

  const handleNextImpactCategory = useCallback(() => {
    setCurrentImpactCategoryIndex((prev) =>
      prev === IMPACT_CATEGORIES.length - 1 ? 0 : prev + 1
    );
  }, []);

  const fetchData = useCallback(() => {
    if (productId) {
      getAllSurplusTransportA2LCA({
        productId,
        setObjects: setData,
      });
    }
  }, [productId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAdd = useCallback(
    (newRow) => {
      // Extract disposal method code
      const disposalMethod =
        typeof newRow.disposal_method === "string"
          ? newRow.disposal_method
          : newRow.disposal_method?.code ||
            newRow.disposal_method?.value ||
            null;

      // Treatment country: backend expects code, but dropdown shows name
      // If value is name, find the code; if it's already code, use it
      let treatmentCountry = null;
      if (newRow.treatment_country) {
        const countryValue =
          typeof newRow.treatment_country === "object"
            ? newRow.treatment_country?.value || newRow.treatment_country?.name
            : newRow.treatment_country;
        // Find country by name or code
        const foundCountry = countryDropdown.find(
          (option) =>
            option.name === countryValue ||
            option.code === countryValue ||
            option.value === countryValue
        );
        treatmentCountry = foundCountry?.code || countryValue;
      }

      addSurplusTransportA2LCA({
        productId,
        surplus_resource: newRow.surplus_resource || null,
        disposal_method: disposalMethod,
        qty: newRow.qty || 0,
        treatment_country: treatmentCountry,
        callback: fetchData,
      });
    },
    [productId, fetchData, countryDropdown]
  );

  const handleEdit = useCallback(
    (updatedRow) => {
      // Extract disposal method code
      const disposalMethod =
        typeof updatedRow.disposal_method === "string"
          ? updatedRow.disposal_method
          : updatedRow.disposal_method?.code ||
            updatedRow.disposal_method?.value ||
            null;

      // Treatment country: backend expects code, but dropdown shows name
      // If value is name, find the code; if it's already code, use it
      let treatmentCountry = null;
      if (updatedRow.treatment_country) {
        const countryValue =
          typeof updatedRow.treatment_country === "object"
            ? updatedRow.treatment_country?.value ||
              updatedRow.treatment_country?.name
            : updatedRow.treatment_country;
        // Find country by name or code
        const foundCountry = countryDropdown.find(
          (option) =>
            option.name === countryValue ||
            option.code === countryValue ||
            option.value === countryValue
        );
        treatmentCountry = foundCountry?.code || countryValue;
      }

      updateSurplusTransportA2LCA({
        productId,
        surplusId: updatedRow.id,
        surplus_resource: updatedRow.surplus_resource || null,
        disposal_method: disposalMethod,
        qty: updatedRow.qty || 0,
        treatment_country: treatmentCountry,
        objects: data,
        setObjects: setData,
      });
    },
    [productId, data, countryDropdown]
  );

  const handleDelete = useCallback(
    (rowId) => {
      deleteSurplusTransportA2LCA({
        productId,
        surplusId: rowId,
        callback: fetchData,
      });
    },
    [productId, fetchData]
  );

  const columns = useMemo(
    () => [
      {
        id: "surplus_resource",
        header: "Surplus Resource",
        accessor: "surplus_resource",
        type: "text",
        placeholder: "User types...",
      },
      {
        id: "disposal_method",
        header: "Current disposal method *",
        accessor: "disposal_method",
        type: "dropdown",
        placeholder: "Select method...",
        isCategory: true,
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "disposal_method",
            disposalMethodDropdown,
            unitDisposalMethods,
            rowId
          ),
      },
      {
        id: "qty",
        header: "Quantity per functional unit *",
        accessor: "qty",
        type: "number",
        placeholder: "User types...",
        unit: (rowId, rowData) =>
          unitFromDropdownOrCache(
            rowData,
            "disposal_method",
            disposalMethodDropdown,
            unitDisposalMethods,
            rowId
          ),
      },
      {
        id: "treatment_country",
        header: "Treatment country",
        accessor: "treatment_country",
        type: "dropdown",
        placeholder: "Select country...",
      },
      {
        id: currentImpactCategory.key,
        header: (
          <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <IconButton
              size="small"
              onClick={handlePreviousImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
            <span>
              {currentImpactCategory.name} [{currentImpactCategory.unit}]
            </span>
            <IconButton
              size="small"
              onClick={handleNextImpactCategory}
              style={{ padding: "4px" }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
            <TableCompareTrigger />
          </div>
        ),
        accessor: currentImpactCategory.key,
        type: "custom",
        optional: true,
        canSort: false,
        cell: ({ row }) => {
          const currentCategory = IMPACT_CATEGORIES[currentImpactCategoryIndex];
          // Backend returns impact categories directly on the object (e.g., global_warming)
          const impactValue =
            row.original[currentCategory.key] ||
            row.original[`impact_${currentCategory.key}`] ||
            row.original.impact_categories?.[currentCategory.key] ||
            0;
          return <CustomInputComponent value={impactValue} readOnly />;
        },
        subtotalUnit: currentImpactCategory.unit,
        subtotalFixed: true,
        width: "200px",
      },
    ],
    [
      currentImpactCategory,
      handlePreviousImpactCategory,
      handleNextImpactCategory,
      unitDisposalMethods,
      disposalMethodDropdown,
      data,
      currentImpactCategoryIndex,
    ]
  );

  const impactCategoryAccessor = currentImpactCategory.key;

  const disableRules = useMemo(
    () => ({
      [impactCategoryAccessor]: () => true, // Make impact category read-only
    }),
    [impactCategoryAccessor]
  );

  const subtotalColumns = useMemo(
    () => ({
      [impactCategoryAccessor]: {
        label: `Total ${currentImpactCategory.name}`,
      },
    }),
    [impactCategoryAccessor, currentImpactCategory.name]
  );

  const memoizedDropdownData = useMemo(
    () => ({
      disposal_method: disposalMethodDropdown || [],
      treatment_country: countryDropdown || [],
    }),
    [disposalMethodDropdown, countryDropdown]
  );

  return (
    <div>
      <CustomTable
        data={data}
        setData={setData}
        columns={columns || []}
        onAdd={handleAdd}
        onEdit={handleEdit}
        onDelete={handleDelete}
        hiddenColumns={hiddenColumns}
        toggleColumnVisibility={toggleColumnVisibility}
        dropdownData={memoizedDropdownData}
        disableRules={disableRules}
        subtotalColumns={subtotalColumns}
        customInputs={useMemo(
          () => ({ [impactCategoryAccessor]: CustomInputComponent }),
          [impactCategoryAccessor]
        )}
        comparisonConfig={{ categories: IMPACT_CATEGORIES }}
      />
    </div>
  );
};

export default SurplusMaterialsTable;
